'use client';

// ── Max Drawdown · trailing (EoD) chart ───────────────────────────────────────
// Figma "Moldura · Exemplo · trailing drawdown": desktop 12071:59891 (792×568),
// mobile 12471:7211 (366×656). Every size is Figma px / 8 in rem. The balance
// area, the end-of-day balance line and the stepped dashed drawdown line are the
// Figma vectors (desktop and mobile exports differ in geometry, so each
// breakpoint gets its own set); grid, close dots, ruler, labels and legend
// swatches are drawn here.

import type { CSSProperties, ReactNode } from 'react';
import { ExampleLabel, FigureFrame } from '../primitives';
import { LockedIcon } from '../Icons';

const ASSETS = '/general-rules-v3/max-drawdown';

const rem = (px: number) => `${px / 8}rem`;
const box = (x: number, y: number, w: number, h: number): CSSProperties => ({
  left: rem(x),
  top: rem(y),
  width: rem(w),
  height: rem(h),
});

// ── Small chart parts ─────────────────────────────────────────────────────────

function Vector({ src, x, y, w, h }: { src: string; x: number; y: number; w: number; h: number }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" aria-hidden draggable={false} className="absolute block max-w-none" style={box(x, y, w, h)} />;
}

function GridLine({ y, w }: { y: number; w: number }) {
  return <div className="absolute h-px bg-black/[0.06]" style={{ left: 0, top: rem(y), width: rem(w) }} />;
}

function AxisLabel({ x, y, right, children }: { x?: number; y: number; right?: number; children: ReactNode }) {
  return (
    <p
      className="absolute mb-0 whitespace-nowrap text-[2rem] leading-[3rem] text-black/40"
      style={{ top: rem(y), ...(x !== undefined ? { left: rem(x) } : {}), ...(right !== undefined ? { right: rem(right) } : {}) }}
    >
      {children}
    </p>
  );
}

/** End-of-day close: 6px green dot with a 1.5px white ring (Figma 9×9 box at x−1.5, y−1.5). */
function CloseDot({ x, y }: { x: number; y: number }) {
  return <div className="absolute rounded-full border-[0.1875rem] border-white bg-[#228e71]" style={box(x - 1.5, y - 1.5, 9, 9)} />;
}

/** Solid "locked at start balance" segment: 2px #001b14 centred on y. */
function LockedLine({ x, y, w }: { x: number; y: number; w: number }) {
  return <div className="absolute bg-[#001b14]" style={box(x, y - 1, w, 2)} />;
}

/** "$2,500" ruler: vertical line with end caps (Figma vector 8×77.875). */
function Ruler({ x, y }: { x: number; y: number }) {
  return (
    <svg viewBox="0 0 8 77.875" className="absolute" style={box(x, y, 8, 77.875)} fill="none" aria-hidden>
      <path d="M4 0.5V77.375M4 0.5H0M4 0.5H8M4 77.375H0M4 77.375H8" stroke="black" strokeOpacity={0.4} />
    </svg>
  );
}

const Y_GRID = [230, 191, 115, 77, 39]; // $50,000 (y 153) is transparent in Figma
const Y_LABELS: [string, number][] = [
  ['$48,000', 218],
  ['$49,000', 179],
  ['$50,000', 141],
  ['$51,000', 103],
  ['$52,000', 65],
  ['$53,000', 27],
];

// ── Charts (desktop 744×300, mobile 318×300) ──────────────────────────────────

const DOTS_DESKTOP: [number, number][] = [
  [0, 150.25],
  [72, 127.375],
  [148, 115.9375],
  [223, 127.375],
  [298, 100.6875],
  [373, 74],
  [449, 51.125],
  [524, 58.75],
  [600, 28.25],
];
const DAYS_DESKTOP: [string, number][] = [
  ['Start', 0],
  ['Day 1', 58],
  ['Day 2', 132],
  ['Day 3', 207],
  ['Day 4', 283],
  ['Day 5', 357],
  ['Day 6', 433],
  ['Day 7', 508],
  ['Day 8', 584],
];

function ChartDesktop() {
  return (
    <div className="absolute inset-0 hidden lg:block" data-ui="figure.maxDrawdownTrailing.chart-desktop">
      {Y_GRID.map((y) => (
        <GridLine key={y} y={y} w={602.727} />
      ))}
      {Y_LABELS.map(([label, y]) => (
        <AxisLabel key={label} right={50} y={y}>
          {label}
        </AxisLabel>
      ))}
      <Vector src={`${ASSETS}/balance-area.svg`} x={0} y={31.2} w={602.727} h={228.8} />
      <Vector src={`${ASSETS}/balance-line.svg`} x={-0.25} y={30.5} w={603.227} h={123.512} />
      <Vector src={`${ASSETS}/drawdown-line.svg`} x={0} y={152.5} w={602.727} h={96.8125} />
      <LockedLine x={441.475} y={153.25} w={161.252} />
      {DOTS_DESKTOP.map(([x, y]) => (
        <CloseDot key={x} x={x} y={y} />
      ))}
      <Ruler x={222} y={134.875} />
      <p className="absolute mb-0 whitespace-nowrap text-[2rem] font-medium leading-[3rem] text-black/64" style={{ left: rem(164), top: rem(162) }}>
        $2,500
      </p>
      <div
        className="absolute flex items-center gap-[0.75rem] rounded-[0.25rem] bg-[#001b14] px-[1rem] py-[0.25rem] text-white"
        style={{ left: rem(399), top: rem(117), width: rem(203.537) }}
        data-ui="figure.maxDrawdownTrailing.locked-tag"
      >
        <LockedIcon className="h-[1.75rem] w-[1.75rem] shrink-0" />
        <p className="mb-0 whitespace-nowrap text-[2rem] font-medium leading-[3rem]">Locked at start balance</p>
      </div>
      {DAYS_DESKTOP.map(([label, x]) => (
        <AxisLabel key={label} x={x} y={276}>
          {label}
        </AxisLabel>
      ))}
    </div>
  );
}

const DOTS_MOBILE: [number, number][] = [
  [-1.93, 150.25],
  [26.965, 127.375],
  [58, 115.9375],
  [87.965, 127.375],
  [117.93, 100.6875],
  [147.895, 74],
  [177.86, 51.125],
  [208.895, 58.75],
  [238.86, 28.25],
];

function ChartMobile() {
  return (
    <div className="absolute inset-0 lg:hidden" data-ui="figure.maxDrawdownTrailing.chart-mobile">
      {Y_GRID.map((y) => (
        <GridLine key={y} y={y} w={241.86} />
      ))}
      {Y_LABELS.map(([label, y]) => (
        <AxisLabel key={label} right={0} y={y}>
          {label}
        </AxisLabel>
      ))}
      <Vector src={`${ASSETS}/balance-area-mobile.svg`} x={0} y={31.2} w={241.75} h={228.8} />
      <Vector src={`${ASSETS}/balance-line-mobile.svg`} x={-0.46} y={30.675} w={242.737} h={123.225} />
      <Vector src={`${ASSETS}/drawdown-line-mobile.svg`} x={0} y={152.5} w={241.75} h={96.8125} />
      <LockedLine x={177.073} y={153.25} w={64.677} />
      {DOTS_MOBILE.map(([x, y]) => (
        <CloseDot key={x} x={x} y={y} />
      ))}
      <Ruler x={86.965} y={134.875} />
      <p className="absolute mb-0 whitespace-nowrap text-[2rem] font-medium leading-[3rem] text-black/64" style={{ left: rem(49.053), top: rem(162) }}>
        $2,500
      </p>
      <AxisLabel x={1.298} y={276}>Start</AxisLabel>
      <AxisLabel x={101.035} y={276}>Day 4</AxisLabel>
      <AxisLabel x={205} y={276}>Day 8</AxisLabel>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────

function Mark({ children }: { children: ReactNode }) {
  return <span className="flex h-[3rem] w-[2rem] shrink-0 items-center justify-center">{children}</span>;
}

function SolidMark({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 16 2" className="h-[0.25rem] w-[2rem]" fill="none" aria-hidden>
      <path d="M0 1H16" stroke={color} strokeWidth={2} />
    </svg>
  );
}

function DashedMark() {
  return (
    <svg viewBox="0 0 16 2" className="h-[0.25rem] w-[2rem]" fill="none" aria-hidden>
      <path d="M0 1H2.28571M16 1H13.7143M5.71429 1H10.2857" stroke="#AC0003" strokeOpacity={0.8} strokeWidth={2} />
    </svg>
  );
}

function LegendRow({ mark, children }: { mark: ReactNode; children: ReactNode }) {
  return (
    <div className="flex w-full items-start gap-[1rem] lg:w-auto">
      <Mark>{mark}</Mark>
      <p className="mb-0 min-w-0 flex-1 text-[2rem] leading-[3rem] text-black/64 lg:flex-none lg:whitespace-nowrap">{children}</p>
    </div>
  );
}

// ── KPI ───────────────────────────────────────────────────────────────────────

function Kpi({ label, value, tone = 'black' }: { label: string; value: string; tone?: 'black' | 'red' }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[0.5rem] lg:flex-none">
      <p className="mb-0 whitespace-nowrap text-[2rem] leading-[3rem] text-black/64">{label}</p>
      <p className={`mb-0 whitespace-nowrap text-[4rem] font-medium leading-[5rem] ${tone === 'red' ? 'text-[#ac0003]' : 'text-black'}`}>
        {value}
      </p>
    </div>
  );
}

// ── Figure ────────────────────────────────────────────────────────────────────

export default function MaxDrawdownTrailing() {
  return (
    <FigureFrame data-ui="figure.maxDrawdownTrailing" className="font-geist">
      <ExampleLabel>EXAMPLE · $50,000 ACCOUNT · $2,500 MAX DRAWDOWN</ExampleLabel>

      <div className="flex w-full gap-[3rem] lg:gap-[6rem]" data-ui="figure.maxDrawdownTrailing.kpis">
        <Kpi label="Start balance" value="$50,000" />
        <Kpi label="Max drawdown" value="$2,500" tone="red" />
      </div>

      <div className="relative h-[37.5rem] w-full" data-ui="figure.maxDrawdownTrailing.chart">
        <ChartDesktop />
        <ChartMobile />
      </div>

      <div className="flex w-full flex-col gap-[1.5rem] lg:flex-row lg:flex-wrap lg:gap-x-[3rem] lg:gap-y-[1rem]" data-ui="figure.maxDrawdownTrailing.legend">
        <LegendRow mark={<SolidMark color="#228E71" />}>End-of-day balance (each day = New York close)</LegendRow>
        <LegendRow mark={<DashedMark />}>Drawdown line (trails)</LegendRow>
        <LegendRow mark={<SolidMark color="#001B14" />}>Locked at start balance</LegendRow>
      </div>
    </FigureFrame>
  );
}
